// src/pages/Home.jsx
import React, { useEffect, useRef, useState } from 'react';
import { 
  FiUsers, 
  FiUserPlus, 
  FiAward, 
  FiBarChart2, 
  FiRefreshCw, 
  FiHome,
  FiDollarSign,
  FiTrendingUp
} from 'react-icons/fi';
import Chart from 'chart.js/auto';
import './HomePage.css';

const Home = () => {
  const [stats, setStats] = useState({
    employees: { total: 0, active: 0, inactive: 0 },
    members: { total: 0, newThisMonth: 0, active: 0, inactive: 0 },
    payments: { 
      total: 0, 
      newThisMonth: 0,
      totalAmount: 0,
      newThisMonthAmount: 0,
      breakdown: []
    },
    departments: 0,
    lastUpdated: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef();
  const monthlyChartRef = useRef();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const safeFetchJson = async (url) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: { 
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      return await res.json();
    } catch (err) {
      console.warn(`Failed to fetch JSON from ${url}:`, err);
      return null;
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [empData, memberData, deptData, payData, memberChartData, paymentChartData] = await Promise.all([
        safeFetchJson(`${BACKEND_URL}/api/employee/stats`),
        safeFetchJson(`${BACKEND_URL}/api/members/stats`),
        safeFetchJson(`${BACKEND_URL}/api/department/stats/count`),
        safeFetchJson(`${BACKEND_URL}/api/payments/stats`),
        safeFetchJson(`${BACKEND_URL}/api/members/chart-data`),
        safeFetchJson(`${BACKEND_URL}/api/payments/chart-data`)
      ]);

      // ✅ FIXED: Separate registration and monthly payments in breakdown
      const paymentBreakdown = payData?.stats?.breakdown || [];
      
      // ✅ Calculate separate totals for registration and monthly
      const registrationPayments = paymentBreakdown.find(p => p._id === 'registration') || { count: 0, totalAmount: 0 };
      const monthlyPayments = paymentBreakdown.find(p => p._id === 'monthly') || { count: 0, totalAmount: 0 };
      const otherPayments = paymentBreakdown.find(p => p._id === 'other') || { count: 0, totalAmount: 0 };

      // ✅ FIXED: Calculate correct totals excluding registration fees where appropriate
      const totalPaymentsCount = payData?.stats?.totalPayments || 0;
      const totalPaymentAmount = payData?.stats?.totalPaymentAmount || 0;
      
      // ✅ For monthly payments display, exclude registration fees
      const monthlyPaymentsCount = monthlyPayments.count;
      const monthlyPaymentsAmount = monthlyPayments.totalAmount;

      setStats({
        employees: {
          total: empData?.totalEmployees || 0,
          active: empData?.activeEmployees || 0,
          inactive: empData?.inactiveEmployees || 0,
        },
        members: {
          total: memberData?.totalMembers || 0,
          newThisMonth: memberData?.newThisMonth || 0,
          active: memberData?.activeMembers || 0,
          inactive: memberData?.inactiveMembers || 0,
        },
        payments: {
          total: totalPaymentsCount,
          newThisMonth: payData?.stats?.newThisMonth || 0,
          totalAmount: totalPaymentAmount,
          newThisMonthAmount: payData?.stats?.newThisMonthAmount || 0,
          breakdown: paymentBreakdown,
          // ✅ ADDED: Separate stats for clearer display
          registrationTotal: registrationPayments.totalAmount,
          monthlyTotal: monthlyPayments.totalAmount,
          registrationCount: registrationPayments.count,
          monthlyCount: monthlyPayments.count,
        },
        departments: deptData?.totalDepartments || 0,
        memberChart: memberChartData?.monthlyData || Array(12).fill(0),
        paymentChart: paymentChartData?.monthlyData || Array(12).fill(0),
        paymentAmounts: paymentChartData?.monthlyAmounts || Array(12).fill(0),
        lastUpdated: new Date(),
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const intervalId = setInterval(fetchStats, 300000); // 5 min
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!chartRef.current || !monthlyChartRef.current) return;

    // Destroy existing charts if they exist
    if (chartRef.current.chartInstance) {
      chartRef.current.chartInstance.destroy();
    }
    if (monthlyChartRef.current.chartInstance) {
      monthlyChartRef.current.chartInstance.destroy();
    }

    // Line chart: Members Growth vs Payments Growth
    const ctx = chartRef.current.getContext('2d');
    chartRef.current.chartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        datasets: [
          {
            label: 'Members Growth',
            data: stats.memberChart || Array(12).fill(0),
            borderColor: '#10B981',
            backgroundColor: 'rgba(16,185,129,0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
          },
          {
            label: 'Payments Growth',
            data: stats.paymentChart || Array(12).fill(0),
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59,130,246,0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true, 
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Members vs Payments Growth'
          },
          legend: {
            position: 'top',
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Count'
            }
          }
        }
      },
    });

    // Bar chart: Payment Amounts by Month
    const monthlyCtx = monthlyChartRef.current.getContext('2d');
    monthlyChartRef.current.chartInstance = new Chart(monthlyCtx, {
      type: 'bar',
      data: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        datasets: [
          {
            label: 'Payment Amount (₹)',
            data: stats.paymentAmounts || Array(12).fill(0),
            backgroundColor: '#8B5CF6',
            borderRadius: 5,
            borderWidth: 1,
          },
          {
            label: 'Number of Payments',
            data: stats.paymentChart || Array(12).fill(0),
            backgroundColor: '#F59E0B',
            borderRadius: 5,
            borderWidth: 1,
          }
        ],
      },
      options: {
        responsive: true, 
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Monthly Payments Overview'
          },
          legend: {
            position: 'top',
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Amount (₹) / Count'
            }
          }
        }
      },
    });

    // Cleanup function
    return () => {
      if (chartRef.current?.chartInstance) {
        chartRef.current.chartInstance.destroy();
      }
      if (monthlyChartRef.current?.chartInstance) {
        monthlyChartRef.current.chartInstance.destroy();
      }
    };
  }, [stats]);

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const statsCards = [
    { 
      icon: <FiUsers />, 
      label: 'Total Employees', 
      value: stats.employees.total, 
      color:'#3B82F6', 
      breakdown: `${stats.employees.active} active / ${stats.employees.inactive} inactive` 
    },
    { 
      icon: <FiUserPlus />, 
      label: 'Total Members', 
      value: stats.members.total, 
      color:'#10B981', 
      breakdown: `${stats.members.active} active / ${stats.members.inactive} inactive` 
    },
    { 
      icon: <FiAward />, 
      label: 'New Members This Month', 
      value: stats.members.newThisMonth, 
      color:'#8B5CF6' 
    },
    { 
      icon: <FiDollarSign />, 
      label: 'Total Payments', 
      value: stats.payments.total, 
      color:'#F43F5E',
      breakdown: `${formatCurrency(stats.payments.totalAmount)} total amount`
    },
    { 
      icon: <FiTrendingUp />, 
      label: 'Monthly Contributions', 
      value: stats.payments.monthlyCount || 0, 
      color:'#EF4444',
      breakdown: `${formatCurrency(stats.payments.monthlyTotal || 0)} total amount`
    },
    { 
      icon: <FiHome />, 
      label: 'Registration Fees', 
      value: stats.payments.registrationCount || 0, 
      color:'#F59E0B',
      breakdown: `${formatCurrency(stats.payments.registrationTotal || 0)} total amount`
    }
  ];

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="header-content">
          <h1 className="welcome-title">Haldoor <span className="brand">Microfinance Company</span></h1>
          <div className="header-actions">
            <button className="refresh-btn" onClick={fetchStats} disabled={loading}>
              <FiRefreshCw className={loading?'spinning':''} /> {loading?'Refreshing...':'Refresh Data'}
            </button>
            {stats.lastUpdated && (
              <span className="last-updated">
                Last updated: {stats.lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="error-alert">
          <span>Error: {error}</span>
          <button className="error-retry" onClick={fetchStats}>Retry</button>
        </div>
      )}

      <section className="stats-section">
        <h2 className="section-title">Overview</h2>
        <div className="stats-grid">
          {statsCards.map((stat, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{color: stat.color}}>
                {stat.icon}
              </div>
              <div className="stat-content">
                <h3 className="stat-value">
                  {loading ? <div className="loading-spinner"></div> : stat.value}
                </h3>
                <p className="stat-label">{stat.label}</p>
                {stat.breakdown && (
                  <p className="stat-breakdown">{stat.breakdown}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Charts Section */}
      <section className="charts-section">
        <div className="chart-card">
          <h3 className="chart-title">Members vs Payments Growth</h3>
          <div className="chart-wrapper">
            <canvas ref={chartRef}></canvas>
          </div>
        </div>
        <div className="chart-card">
          <h3 className="chart-title">Monthly Payments Analysis</h3>
          <div className="chart-wrapper">
            <canvas ref={monthlyChartRef}></canvas>
          </div>
        </div>
      </section>

      {/* Payment Breakdown Section */}
      {stats.payments.breakdown && stats.payments.breakdown.length > 0 && (
        <section className="breakdown-section">
          <h2 className="section-title">Breakdown of Payment Types</h2>
          <div className="breakdown-grid">
            {stats.payments.breakdown.map((type, index) => (
              <div key={index} className="breakdown-card">
                <h4 className="breakdown-type">
                  {type._id === 'registration' ? 'Registration Fees' : 
                   type._id === 'monthly' ? 'Monthly Contributions' : 
                   type._id || 'Other'}
                </h4>
                <p className="breakdown-count">{type.count} payments</p>
                <p className="breakdown-amount">
                  {formatCurrency(type.totalAmount)}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;